import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ChevronDown, MessageSquare, Star } from "lucide-react";
import Avatar from "../common/Avatar";
import {
  formatPositionValue,
  formatStaffRole,
  formatTypeOfTender,
} from "../../constants/agentTenderChoices";
import {
  formatCompactMoney,
  formatHeightMeters,
} from "../../utils/agentRequestFigures";

// A single response (another agency's player pitched at one of your requests),
// restyled to the V2 short-profile TRIAGE ROW (design doc 03, screen 7b):
// identity + status badge, fact chips, the ask, responding agency + message
// snippet, and actions. "Full player profile" is a plain link into the
// existing profile page in a NEW TAB, so the inbox never loses its place; a
// message sent with the pitch expands inline via the chevron (no message =
// no chevron). The posting side is the club-owner here, so the responding
// agency + agent and the player are all shown — nothing is anonymized on
// this internal surface (Rule 8). Accept/Reject/Shortlist keep the existing
// Phase 1.5 pipeline untouched (accept opens the anonymized chat first).
const statusBadge = (status, isShortlisted) => {
  if (status === "accepted")
    return { text: "Accepted", cls: "bg-[#ECFDF5] text-[#047857]" };
  if (status === "rejected")
    return { text: "Declined", cls: "bg-red-50 text-red-600" };
  if (isShortlisted)
    return { text: "Shortlisted", cls: "bg-[#FEF3C7] text-[#B45309]" };
  return { text: "New", cls: "bg-[#E8F1FF] text-[#1B6FE0]" };
};

const chipCls =
  "text-[11.5px] text-[#334155] bg-[#F1F5F9] px-2 py-[3px] rounded-full whitespace-nowrap";

// Origin chip label for the Shortlist tab: which of your requests this
// response belongs to (the design's "From: [request]" — the star never
// deletes anything). The inbox tender ref carries no staff_role, so staff
// requests fall back to the position fields it does carry.
export const originLabel = (tender) => {
  if (!tender) return null;
  const title =
    tender.tender_category === "staff"
      ? formatStaffRole(tender.staff_role) ||
        formatPositionValue(tender.position, tender.other_staff_position)
      : formatPositionValue(tender.position, tender.other_staff_position);
  const type = formatTypeOfTender(tender.type_of_tender);
  return [title, type].filter(Boolean).join(" · ");
};

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

// "Contract until 2027" (design 7b shows the YEAR, not the full date).
// Tolerant of the serializer's shape: ISO date string ("2027-06-30") or an
// epoch number (Wyscout stores seconds or milliseconds). Unknown → null,
// which hides the chip entirely.
export const contractUntilYear = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-\d{2}-\d{2}/);
    if (m) return m[1];
  }
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Epoch seconds vs milliseconds: anything below 1e11 is seconds.
  const d = new Date(n < 1e11 ? n * 1000 : n);
  const year = d.getUTCFullYear();
  return year > 1970 ? String(year) : null;
};

const ResponseRow = ({
  response,
  busy,
  onAccept,
  onReject,
  onToggleShortlist,
  showOrigin = false,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const {
    player,
    tender,
    content,
    transfer_fee,
    transfer_fee_currency,
    free_agent,
    status,
    is_shortlisted,
    responding_agency,
    responding_agent,
    channel_id,
    created_at,
  } = response;

  const p = player || {};
  const playerName = p.name || "Unnamed player";
  const agentName = responding_agent?.name || responding_agent?.display_name;
  const agentAvatar =
    responding_agent?.profile_picture_url || responding_agent?.avatar;
  const agencyName = responding_agency?.name;
  const badge = statusBadge(status, is_shortlisted);
  const isActive = status !== "accepted" && status !== "rejected";

  // Short-profile summary line: age · position · current club (design 7b
  // "24 · Central Midfielder · FC Aarau" — the club lives HERE, not a chip).
  const summary = [p.age, formatPositionValue(p.position), p.club_name]
    .filter(Boolean)
    .join(" · ");

  // The ask, in the design's compact figure grammar ("€1.8M transfer fee").
  // Missing currency on a stored fee reads as EUR — the platform default for
  // a pitch fee, matching utils/currencyFormatter.formatTransferFee.
  const fee = formatCompactMoney(transfer_fee, transfer_fee_currency || "EUR");
  const asking = free_agent
    ? "Free agent"
    : fee
    ? `${fee} transfer fee`
    : "Negotiable";

  // Fact chips from the short profile — only what the payload carries.
  // Contract-until and MV joined the serializer with the 2026-07-22 BE
  // follow-up; on older payloads they're absent and the chips stay hidden.
  const height = formatHeightMeters(p.height);
  const foot =
    p.foot === "Left" || p.foot === "Right" ? `${p.foot} foot` : p.foot || null;
  const contractYear = contractUntilYear(p.contract_until);
  // A zero MV is "unknown", not a figure — chip hidden like any empty field.
  const marketValue =
    Number(p.market_value) > 0
      ? formatCompactMoney(p.market_value, p.market_value_currency || "EUR")
      : null;

  // Existing profile page, same id convention as every player surface
  // (wyscout id first). New tab so the inbox keeps its place.
  const profileId = p.wyscout_id || p.id;
  const origin = showOrigin ? originLabel(tender) : null;

  return (
    <div
      className={`border-b border-[#F1F5F9] last:border-b-0 ${
        status === "accepted" ? "bg-[#F7FDF9]" : ""
      }`}
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 px-4 lg:px-[18px] py-3.5">
        {/* Unread marker: filled only while the response awaits a decision. */}
        <span
          className={`hidden lg:block flex-none w-2 h-2 rounded-full ${
            isActive ? "bg-[#2283FF]" : "bg-transparent"
          }`}
        />

        {/* Player identity + status badge + short summary */}
        <div className="flex items-center gap-2.5 lg:w-[270px] min-w-0 flex-none">
          <Avatar src={p.image} fallbackText={playerName} size={40} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[14px] font-bold text-[#0F172A] truncate">
                {playerName}
              </span>
              <span
                className={`flex-none text-[10px] font-extrabold px-[7px] py-px rounded-full ${badge.cls}`}
              >
                {badge.text}
              </span>
            </div>
            <div className="text-[12px] text-[#64748B] truncate">{summary}</div>
            {origin && (
              <span className="inline-flex mt-1 text-[11px] font-bold text-[#1B6FE0] bg-[#E8F1FF] px-2.5 py-1 rounded-full whitespace-nowrap max-w-full overflow-hidden text-ellipsis">
                From request: {origin}
              </span>
            )}
          </div>
        </div>

        {/* Fact chips */}
        <div className="flex gap-1.5 flex-wrap lg:flex-1 min-w-0 items-center">
          {free_agent && (
            <span className="text-[11.5px] font-bold text-[#1B6FE0] bg-[#E8F1FF] px-2 py-[3px] rounded-full whitespace-nowrap">
              Free player
            </span>
          )}
          {height && <span className={chipCls}>{height}</span>}
          {foot && <span className={chipCls}>{foot}</span>}
          {contractYear && (
            <span className={chipCls}>Contract until {contractYear}</span>
          )}
          {marketValue && <span className={chipCls}>MV {marketValue}</span>}
        </div>

        {/* The ask */}
        <div className="lg:w-[140px] flex-none flex lg:block items-baseline gap-2">
          <div className="text-[10.5px] text-[#94A3B8] font-bold uppercase tracking-[0.06em]">
            Asking
          </div>
          <div className="text-[13px] font-bold text-[#0F172A] whitespace-nowrap">
            {asking}
          </div>
        </div>

        {/* Responding agency + agent + message snippet */}
        <div className="lg:w-[190px] flex-none min-w-0 flex items-center gap-2">
          <span className="lg:hidden flex-none">
            <Avatar src={agentAvatar} fallbackText={agentName} size={22} />
          </span>
          <div className="min-w-0">
            {agencyName && (
              <div className="text-[12px] font-semibold text-[#0F172A] truncate">
                {agencyName}
              </div>
            )}
            <div className="text-[11px] text-[#64748B] truncate">
              {agentName}
              {content ? `${agentName ? " · " : ""}"${content}"` : ""}
            </div>
          </div>
        </div>

        {/* Actions — same service calls as before, V2 dress only. */}
        <div className="flex items-center gap-[7px] flex-wrap flex-none">
          {profileId && (
            <a
              href={`/search/player/${profileId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-lg border border-[#BFDBFE] bg-[#E8F1FF] text-[#1B6FE0] text-[12px] font-semibold whitespace-nowrap hover:bg-blue-100"
            >
              Full player profile
              <ArrowUpRight className="w-3 h-3" />
            </a>
          )}
          {status === "accepted" ? (
            <button
              onClick={() =>
                navigate(channel_id ? `/inbox?channel=${channel_id}` : "/inbox")
              }
              className="inline-flex items-center gap-1.5 h-[34px] px-3.5 bg-[#2283FF] text-white rounded-lg text-[12px] font-semibold hover:bg-blue-600 whitespace-nowrap"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Open chat
            </button>
          ) : status === "rejected" ? (
            <span className="text-[11.5px] text-gray-400 whitespace-nowrap">
              You declined this response.
            </span>
          ) : (
            <>
              <button
                onClick={() => onToggleShortlist(response)}
                disabled={busy}
                title={is_shortlisted ? "Remove from shortlist" : "Add to shortlist"}
                aria-label={
                  is_shortlisted ? "Remove from shortlist" : "Add to shortlist"
                }
                className={`w-[34px] h-[34px] rounded-lg border inline-flex items-center justify-center disabled:opacity-50 ${
                  is_shortlisted
                    ? "border-[#FDE68A] bg-[#FFFBEB] text-[#B45309]"
                    : "border-[#E2E8F0] bg-white text-[#64748B] hover:bg-gray-50"
                }`}
              >
                <Star
                  className="w-3.5 h-3.5"
                  fill={is_shortlisted ? "currentColor" : "none"}
                />
              </button>
              <button
                onClick={() => onAccept(response)}
                disabled={busy}
                className="h-[34px] px-3.5 bg-[#047857] text-white rounded-lg text-[12px] font-semibold hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
              >
                Accept
              </button>
              <button
                onClick={() => onReject(response)}
                disabled={busy}
                className="h-[34px] px-3 border border-[#E2E8F0] bg-white text-[#64748B] rounded-lg text-[12px] font-semibold hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
              >
                Reject
              </button>
            </>
          )}
          {/* Message expansion: the chevron exists only when the agent sent a
              note with the pitch (design rule: no message = no chevron). */}
          {content && (
            <button
              onClick={() => setExpanded((v) => !v)}
              title={expanded ? "Hide message" : "Show message"}
              aria-label={expanded ? "Hide message" : "Show message"}
              className="w-[34px] h-[34px] rounded-lg border border-[#BFDBFE] bg-[#E8F1FF] text-[#1B6FE0] inline-flex items-center justify-center hover:bg-blue-100"
            >
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  expanded ? "rotate-180" : ""
                }`}
              />
            </button>
          )}
        </div>
      </div>

      {/* Inline message bubble (design: open state under the row) */}
      {expanded && content && (
        <div className="px-4 lg:pl-[74px] lg:pr-[18px] pb-3.5">
          <div className="bg-[#F8FAFC] border border-[#EEF2F7] rounded-tl rounded-xl px-3.5 py-3 max-w-[760px] flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5 text-[11px] text-[#94A3B8] flex-wrap">
              <MessageSquare className="w-3 h-3 flex-none" />
              <b className="text-[#475569]">
                Message from {agentName || "the agent"}
              </b>
              {agencyName && <span>· {agencyName}</span>}
              <span>· sent with the pitch</span>
              {created_at && <span>· {fmtDate(created_at)}</span>}
            </div>
            <div className="text-[12.5px] text-[#334155] leading-relaxed whitespace-pre-wrap">
              {content}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseRow;
